import clsx from "clsx";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import React from "react";

import { RouteName } from "@/consts/RouteName";
import { StackImageIcon } from "@/features/common/components/icons/StackImageIcon";
import { PostActionButton } from "@/features/post/components/PostActionButton";
import { RichTextContent } from "@/features/post/components/common/RichTextContent";
import { useUserContext } from "@/features/user/contexts/useUserContext";
import {
  PostTypeEnum,
  SubscriptionPlanStatusEnum,
  usePostInfoWithLoggedInUserQuery,
} from "@/generated/generated-hooks";

import { Author, CardDate, ImageArea, Title } from "./components";
import { ImageCardActionBar } from "./components/ImageCardActionBar";
import { ImageCardProps } from "./type";

const NormalImageCard: React.FC<ImageCardProps> = ({
  isRender = true,
  post,
}) => {
  const {
    _id,
    writer,
    createdAt,
    title,
    content,
    slug,
    postStatus,
    previewImage,
    images,
    postType,
    previewContent,
    publishAt,
    isPublished,
    subscriptionPlan,
  } = post;

  const { route } = useRouter();
  const { t } = useTranslation("subscriptionPlan");
  const { user, ready } = useUserContext();
  const hasImage = !!previewImage || images?.length > 0;

  const isPreview = route === `${RouteName.NewPost}/[slug]`;
  const isOwner = user?._id === writer?._id;
  const isLanding = !user && route === `${RouteName.Landing}`;

  // TODO Not quite understanding the skip
  const { data: postInfo } = usePostInfoWithLoggedInUserQuery({
    variables: { slug },
    context: { batch: true },
    skip: !user || !hasImage || isPreview || !isRender || isLanding || !ready,
  });

  const renderContent = () => {
    // or if post type === article, check if post.isContentLocked && does the userSubsceiberd
    if (postType === PostTypeEnum.Article) {
      // if user not subscribed to subPlan with UNLOCK_CONTENT --> display preview only
      return (
        <RichTextContent
          isPreview={isPreview}
          isOwner={isOwner}
          isLanding={isLanding}
          content={previewContent}
          isImageCard={true}
        />
      );
    }

    return (
      <RichTextContent
        isPreview={isPreview}
        isOwner={isOwner}
        isLanding={isLanding}
        content={content?.serialized}
        isImageCard={true}
      />
    );
  };

  return (
    <div
      className={clsx(
        "relative flex flex-col w-full border border-solid border-gray-200 overflow-hidden select-none rounded-xl",
        isPreview ? "bg-white" : "bg-transparent",
      )}
      onContextMenu={(e) => e.preventDefault()}
      data-test-id="NormalImageCard-container"
      data-test-type="imageCard"
    >
      {/* ImageArea */}
      {(hasImage || isPreview) && (
        <ImageArea post={{ ...post, ...(postInfo?.post ?? {}) }} />
      )}

      {/* Information */}
      <div
        className={clsx(
          "flex flex-col min-w-0 p-4",
          !hasImage && !isPreview ? "mt-8" : "mt-0",
        )}
      >
        <div className="w-full flex justify-between">
          <span
            className={clsx(
              "rounded-lg text-xs px-2 py-1 text overflow-ellipsis overflow-hidden whitespace-nowrap",
              subscriptionPlan
                ? subscriptionPlan.subscriptionPlanStatus ===
                  SubscriptionPlanStatusEnum.Published
                  ? "bg-primary text-white"
                  : "bg-gray-100 text-primary"
                : "bg-gray-100 text-gray-700",
            )}
          >
            {subscriptionPlan ? subscriptionPlan.title : t("freePlan")}
          </span>
          <div className="ml-6">
            <CardDate date={isPublished ? createdAt : publishAt} />
          </div>
        </div>

        {/* Title */}
        <Title title={title} slug={slug} isPreview={isPreview} postId={_id} />

        {/* Content */}
        {content && renderContent()}

        <div className="flex justify-between">
          {/* Author */}
          <div className="mt-4 min-w-0 truncate">
            <Author author={writer} />
          </div>

          {/* Action Bar */}
          {!isPreview && (
            <div className="flex mt-4">
              <ImageCardActionBar
                post={post}
                hasCurrentUserLike={postInfo?.post?.hasCurrentUserLike}
              />
              {!isLanding && (
                <PostActionButton
                  postDetail={{
                    title,
                    postStatus,
                    createdAt,
                    _id,
                    writerId: writer._id,
                    hasCurrentUserReport: postInfo?.post?.hasCurrentUserReport,
                    slug,
                  }}
                />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Utilities and Multi image indicator */}
      <div className="absolute flex top-2 right-4 z-40 items-center">
        {images?.length > 1 && <StackImageIcon />}
      </div>
    </div>
  );
};

export default NormalImageCard;
